import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@mui/material";
import "./logs.css"; // Import external CSS

const LogsPage = ({ data = [] }) => {
  // Sort data by timestamp - latest first
  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) {
      console.warn("LogsPage received invalid data:", data);
      return [];
    }

    return [...data].sort((a, b) => {
      const timestampA = a.Timestamp;
      const timestampB = b.Timestamp;

      // Handle missing timestamps
      if (!timestampA && !timestampB) return 0;
      if (!timestampA) return 1; // Put records without timestamp at the end
      if (!timestampB) return -1;

      // Try to parse as Date objects for proper comparison
      const dateA = new Date(timestampA);
      const dateB = new Date(timestampB);

      // Check if dates are valid
      const isValidA = !isNaN(dateA.getTime());
      const isValidB = !isNaN(dateB.getTime());

      if (isValidA && isValidB) {
        // Both are valid dates - sort descending (latest first)
        return dateB.getTime() - dateA.getTime();
      } else if (isValidA && !isValidB) {
        return -1; // Valid date comes first
      } else if (!isValidA && isValidB) {
        return 1; // Valid date comes first
      } else {
        // Both are invalid dates, sort as strings descending
        return timestampB.localeCompare(timestampA);
      }
    });
  }, [data]);

  // Format timestamp for display - keep original format
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    
    // Return original timestamp as-is (e.g., "2025-07-02T10:37:42.164816")
    return timestamp;
  };

  if (!Array.isArray(data)) {
    console.warn("LogsPage received invalid data:", data);
    return <Typography color="error">No logs available</Typography>;
  }

  if (data.length === 0) {
    return (
      <Box className="logs-container">
        <Typography variant="h5" className="logs-title">
          Audit Logs (0)
        </Typography>
        <Paper className="logs-table-wrapper">
          <Typography 
            variant="body1" 
            sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}
          >
            No audit logs available
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="logs-container">
      <Typography variant="h5" className="logs-title">
        Audit Logs ({sortedData.length})
      </Typography>

      <Paper className="logs-table-wrapper">
        <Table className="logs-table">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Timestamp</strong>
                <Typography variant="caption" display="block" color="text.secondary">
                  (Latest First)
                </Typography>
              </TableCell>
              <TableCell><strong>Modified By</strong></TableCell>
              <TableCell><strong>Action</strong></TableCell>
              <TableCell><strong>Resource Type</strong></TableCell>
              <TableCell><strong>Resource Name</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((log, idx) => (
              <TableRow key={idx} className="logs-row">
                <TableCell>
                  <Typography variant="body2">
                    {formatTimestamp(log.Timestamp)}
                  </Typography>
                </TableCell>
                <TableCell>{log.ModifiedBy || "-"}</TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: log.Action ? 'medium' : 'normal',
                      color: log.Action ? 'primary.main' : 'text.secondary'
                    }}
                  >
                    {log.Action || "-"}
                  </Typography>
                </TableCell>
                <TableCell>{log.ResourceType || "-"}</TableCell>
                <TableCell>{log.ResourceName || "-"}</TableCell>
                <TableCell>
                  <Typography 
                    variant="body2"
                    sx={{
                      color: log.Status === 'Success' || log.Status === 'success' 
                        ? 'success.main' 
                        : log.Status === 'Failed' || log.Status === 'failed' || log.Status === 'Error' || log.Status === 'error'
                        ? 'error.main'
                        : 'text.primary'
                    }}
                  >
                    {log.Status || "-"}
                  </Typography>
                </TableCell>
                <TableCell>{log.Email || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default LogsPage;